import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Sun, Calendar, TrendingUp, Lock, Compass, Sparkles, Star, Copy, Info, X } from 'lucide-react';
import { getDailyAdvice } from '@/utils/numerologyDescriptions';
import { VIPSubscription } from '@/components/VIPSubscription';
import VIPModal from '@/components/VIPModal';
import VIPSuccessModal from '@/components/VIPSuccessModal';
import WeeklyCycleCard from '@/components/WeeklyCycleCard';
import { calculatePersonalYear, calculatePersonalMonth, calculatePersonalDay, getWeeklyCycle } from '@/utils/weeklyCycle';
import UserMenu from '@/components/UserMenu';
import { useAuth } from '@/context/AuthContext';
import { copyToClipboard } from '@/utils/copyToClipboard';

// Skaičiavimo info komponentas
const CalculationInfo = ({ title, formula, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 z-50"
      style={{ backgroundColor: 'rgba(15, 15, 35, 0.95)' }}
      onClick={onClose}
    >
      <div 
        className="bg-slate-900 border border-amber-500/30 rounded-lg p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-amber-400">{title}</h4>
          <button onClick={onClose} className="text-foreground/50 hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-sm text-foreground/80 space-y-3">
          {formula}
        </div>
      </div>
    </div>
  );
};

// Mažas info mygtukas
const InfoButton = ({ onClick }) => (
  <button 
    onClick={onClick}
    className="ml-2 text-amber-400/50 hover:text-amber-400 transition-colors"
    title="Kaip apskaičiuota?"
  >
    <Info className="w-4 h-4" />
  </button>
);

// Asmeninių metų temos - tai yra PAGRINDAS visoms prognozėms
const getPersonalYearTheme = (number) => {
  const themes = {
    1: {
      theme: "Naujos pradžios ir iniciatyvos metai",
      context: "Šie metai ženklina naujo 9-erių metų ciklo pradžią. Visata atveria langą jūsų iniciatyvai – drąsūs žingsniai, nauji projektai ir asmeninė lyderystė yra šių metų raktiniai žodžiai. Kiekviena mėnesio prognozė turėtų būti skaitoma per šią prizmę: 'Kaip tai padeda man pradėti nauja?'"
    },
    2: {
      theme: "Partnerystės ir kantrybės metai",
      context: "Šie metai skirti santykių stiprinimui ir bendradarbiavimui. Energija kviečia į kompromisą, diplomatiją ir kantrų laukimą. Kiekvienas mėnuo šiais metais moko suprasti kito perspektyvą ir veikti kartu, ne prieš."
    },
    3: {
      theme: "Kūrybos ir saviraiškos metai",
      context: "Šie metai kviečia jus spinduliuoti! Kūrybinė energija bėga per jus tarsi upė – leiskite jai tekėti per meną, bendravimą, džiaugsmą. Kiekvienas mėnuo šiais metais atneša galimybę išreikšti save naujai."
    },
    4: {
      theme: "Pamatų kūrimo ir stabilumo metai",
      context: "Šie metai skirti tvirto pagrindo statymui. Sunkus darbas, disciplina ir metodiškas požiūris atneš ilgalaikius rezultatus. Kiekvienas mėnuo šiais metais yra plyta jūsų ateities mūre – nepraleiskite nė vieno."
    },
    5: {
      theme: "Pokyčių ir laisvės metai",
      context: "Šie metai neša didžiulius pokyčius ir naujas galimybes. Energija kviečia į nuotykius, keliones ir išėjimą iš komforto zonos. Kiekvienas mėnuo šiais metais gali atnešti netikėtumų – būkite atviri ir lankstūs."
    },
    6: {
      theme: "Šeimos, namų ir atsakomybės metai",
      context: "Šie metai skirti rūpesčiui ir harmonijai. Šeima, namai, artimi santykiai tampa centru. Grožis ir meilė teikia ramybę. Kiekvienas mėnuo šiais metais moko mylėti ir rūpintis – tiek kitais, tiek savimi."
    },
    7: {
      theme: "Dvasinio augimo ir išminties metai",
      context: "Šie metai kviečia į vidų – meditaciją, refleksiją, mokymąsi. Intuicija šiais metais nepaprastai stipri. Kiekvienas mėnuo neša gilesnį supratimą ir atsakymus į klausimus, kuriuos seniai nešiojatės."
    },
    8: {
      theme: "Gausos, valdžios ir sėkmės metai",
      context: "Šie metai suteikia galimybę materialinei sėkmei ir karjeros šuoliui. Jūsų gebėjimas daryti įtaką realybei sustiprinamas. Kiekvienas mėnuo šiais metais neša galimybes valdyti, kurti ir auginti turtą."
    },
    9: {
      theme: "Užbaigimo ir transformacijos metai",
      context: "Šie metai skirti paleisti seną ir pasiruošti naujam ciklui. Tai užbaigimo, atleidimo ir transformacijos laikas. Kiekvienas mėnuo šiais metais kviečia atsisakyti to, kas nebetarnauja, ir atverti erdvę naujai pradžiai kitais metais."
    }
  };
  return themes[number] || themes[1];
};

// Energetiniai aprašymai pagal skaičių - SAVAITĖ (trumpalaikis veiksmas)
const getWeeklyEnergyDescription = (number, lifePath) => {
  const descriptions = {
    1: {
      energy: "Ši savaitė pulsuoja naujos pradžios energija. Jūsų vidinė ugnis kyla – tai laikas, kai iniciatyva ir drąsa natūraliai randa kelią į paviršių.",
      flow: "Energija lengviausiai teka per asmeninę iniciatyvą, nepriklausomus sprendimus ir lyderystę. Šią savaitę Visata palaiko tuos, kurie drįsta eiti pirmi.",
      resistance: "Galite susidurti su pasipriešinimu, jei bandysite pernelyg prisitaikyti prie kitų ar lauksite leidimo veikti. Energetinė kliūtis slypi neapsisprendime.",
      conscious: "Šią savaitę priimk vieną sprendimą be kitų patvirtinimo. Mažas, bet savarankiškas žingsnis."
    },
    2: {
      energy: "Savaitė apsigaubia subtilia harmonijos energija. Intuicija stiprėja, o santykių gijos tampa ryškesnės. Tai metas, kai diplomatija ir kantrybė tampa jūsų sąjungininkais.",
      flow: "Energija lengviausiai teka per bendradarbiavimą, klausymąsi ir emocines jungtis. Partnerystės – tiek asmeninės, tiek profesinės – šią savaitę atneša gilesnę prasmę.",
      resistance: "Pasipriešinimą pajusite, jei bandysite primesti savo valią arba skubėsite. Energetinė kliūtis slypi nekantrume ir ego dominavime.",
      conscious: "Šią savaitę atidžiai išklausyk bent vieną žmogų – nereaguodamas, o tiesiog būdamas. Vienas pokalbis gali viską pakeisti."
    },
    3: {
      energy: "Kūrybinė energija šią savaitę tiesiog liejasi. Tai laikas, kai žodžiai, idėjos ir meninė raiška tampa jūsų natūraliu kanalų. Džiaugsmas – jūsų kompasas.",
      flow: "Energija lengviausiai teka per saviraiškos formas: kalbėjimą, rašymą, meną, socialinę veiklą. Visa, kas skleidžia šviesą ir džiaugsmą, yra palaikoma.",
      resistance: "Pasipriešinimą sukels bandymai slopinti savo jausmus ar likti rimtiems ten, kur norisi žaisti. Energetinė kliūtis – perdėtas seriozumas.",
      conscious: "Šią savaitę leisk sau vieną spontanišką kūrybinę akimirką – daina, piešinys, šokis. Džiaugsmas atveria srautą."
    },
    4: {
      energy: "Tvirtumas ir struktūra – šios savaitės raktiniai žodžiai. Energija kviečia įsitvirtinti, sukurti pamatus, sutvarkyti tai, kas buvo atidėta. Disciplina tampa lengvesnė.",
      flow: "Energija lengviausiai teka per sistemingą darbą, planavimą ir praktinius veiksmus. Tai puikus laikas tvarkyti finansus, organizuoti erdves, kurti rutinas.",
      resistance: "Pasipriešinimą pajusite, jei sieksite greitų rezultatų ar bandysite apeiti procesą. Energetinė kliūtis – nekantrumas ir paviršutiniškumas.",
      conscious: "Šią savaitę užbaik vieną seniai atidėtą darbą. Net mažas užbaigimas atlaisvins energiją."
    },
    5: {
      energy: "Pokyčių vėjai pučia šią savaitę. Energija kviečia į laisvę, nuotykius ir naujų horizontų tyrinėjimą. Rutina gali kelti nerimą – ir tai normalu.",
      flow: "Energija lengviausiai teka per lankstumą, spontaniškumą ir atvirumą naujam. Kelionės, nauji žmonės, netikėtos galimybės – visa tai jus palaiko.",
      resistance: "Pasipriešinimą sukels bandymai kontroliuoti viską arba laikytis griežtų planų. Energetinė kliūtis – baimė ir prisirišimas prie to, kas pažįstama.",
      conscious: "Šią savaitę padaryk vieną mažą dalyką kitaip nei įprastai. Naujas maršrutas, naujas skonis – atvirumas atveria srautą."
    },
    6: {
      energy: "Meilės ir rūpesčio energija apgaubia šią savaitę. Širdis tampa jūsų centru – santykiai, šeima, namai reikalauja ir duoda daugiausiai. Grožis teikia ramybę.",
      flow: "Energija lengviausiai teka per rūpinimąsi kitais, namų harmonizavimą, meninius projektus ir viską, kas susiję su grožiu ir komfortu.",
      resistance: "Pasipriešinimą sukels bandymai ignoruoti artimųjų poreikius arba, priešingai, pamiršti save dėl kitų. Energetinė kliūtis – disbalansas tarp davimo ir gavimo.",
      conscious: "Šią savaitę sukurk vieną gražų momentą savo namuose – gėlė, žvakė, akimirka su artimu žmogumi."
    },
    7: {
      energy: "Vidinės išminties savaitė. Energija kviečia į tylą, refleksiją ir gilesnius klausimus. Intuicija stipri – pasitikėkite vidiniais atsakymais, net jei logika prieštarauja.",
      flow: "Energija lengviausiai teka per meditaciją, skaitymą, mokymąsi, vienatvės akimirkas ir visa, kas maitina sielą. Ramybė – ne prabanga, o būtinybė.",
      resistance: "Pasipriešinimą sukels pernelyg intensyvus socialinis gyvenimas ar bandymai užpildyti visą laiką veikla. Energetinė kliūtis – bėgimas nuo savęs.",
      conscious: "Šią savaitę skirk 15 minučių tylai kasdien. Ne meditacijai būtinai – tiesiog tylai."
    },
    8: {
      energy: "Galios ir gausos energija šią savaitę. Tai metas, kai jūsų gebėjimas daryti įtaką realybei sustiprinamas. Materialūs tikslai tampa pasiekiami.",
      flow: "Energija lengviausiai teka per ambicingus projektus, finansinius sprendimus, derybas ir viską, kas susiję su valdymu bei organizavimu.",
      resistance: "Pasipriešinimą sukels baimė prisiimti atsakomybę arba, priešingai, bandymai kontroliuoti per daug. Energetinė kliūtis – godumas arba bejėgiškumas.",
      conscious: "Šią savaitę priimk vieną finansinį ar karjeros sprendimą, kurį atidėliojote. Veiksmas atveria galimybes."
    },
    9: {
      energy: "Užbaigimo ir transcendencijos energija. Šią savaitę natūraliai kyla noras paleisti tai, kas nebetarnauja, ir atverti erdvę naujam ciklui.",
      flow: "Energija lengviausiai teka per atleidimą, užbaigimą, savanorystę ir viską, kas didesnio nei jūs patys. Jūsų dovanos kitiems sugrįžta dešimteriopai.",
      resistance: "Pasipriešinimą sukels bandymai laikytis to, kas baigėsi, arba pradėti naujus projektus prieš užbaigiant senus. Energetinė kliūtis – prisirišimas.",
      conscious: "Šią savaitę atleisk vienam žmogui – garsiai ar tyliai. Arba atleisk sau. Vienas atleidimas atveria naują erdvę."
    }
  };
  
  return descriptions[number] || descriptions[1];
};

// Mėnesio energijos aprašymai - MĖNUO (ilgalaikė būsena/požiūris)
const getMonthlyEnergyDescription = (number, lifePath) => {
  const descriptions = {
    1: {
      theme: "Naujos pradžios",
      energy: "Šis mėnuo ženklina naujo ciklo pradžią. Visata atveria langą, per kurį jūsų iniciatyva ir drąsa gali įgauti formą. Tai laikas sodinti sėklas – ne tik planuoti, bet ir veikti.",
      lightFlow: "Energija jums palankiausia, kai veikiate savarankiškai, prisiimate lyderystę ir nebijote būti pirmi. Originalios idėjos šį mėnesį turi ypatingą jėgą.",
      shadowFlow: "Atsargiai su impulsyvumu – nors iniciatyva svarbi, pernelyg skubus šuolis be pasiruošimo gali išbalansuoti energiją. Taip pat saugokitės perdėto ego.",
      conscious: "Visą šį mėnesį praktikuok drąsą būti savimi. Ne kartą, o kaip būseną – kiekvieną dieną pasirinkdamas autentiškumą virš konformizmo."
    },
    2: {
      theme: "Partnerystės ir kantrybės",
      energy: "Mėnuo, kai santykiai tampa jūsų mokytoju. Energija kviečia į bendradarbiavimą, kompromisą ir švelną kantrybę. Tai, ko negalite pasiekti vieni – pasieksite su kitais.",
      lightFlow: "Energija jums palankiausia, kai veikiate komandoje, klausotės prieš kalbėdami ir leidžiate dalykams vystytis savo ritmu. Subtilumas – jūsų jėga.",
      shadowFlow: "Atsargiai su neapsisprendimu ir pernelyg ilgu laukimu. Taip pat saugokitės nuo kitų emocijų absorbavimo – jūsų jautrumas šį mėnesį ypač stiprus.",
      conscious: "Visą šį mėnesį praktikuok kantrybę kaip būseną. Ne reaktyvų laukimą, o sąmoningą pasitikėjimą, kad viskas vyksta savo laiku."
    },
    3: {
      theme: "Kūrybos ir saviraiškos",
      energy: "Šis mėnuo tiesiog kviečia jus spinduliuoti. Kūrybinė energija bėga per jus tarsi upė – klausimas tik, ar leisite jai tekėti, ar bandysite sulaikyti.",
      lightFlow: "Energija jums palankiausia per bet kokią saviraiškos formą: kalbėjimą, rašymą, meną, šokį, socialinį bendravimą. Džiaugsmas – ne pramoga, o navigacija.",
      shadowFlow: "Atsargiai su išsibarstymu ir paviršutiniškumu. Energija plati, bet ne gili šį mėnesį. Taip pat saugokitės nuo perdėto kritiškumo sau.",
      conscious: "Visą šį mėnesį praktikuok džiaugsmą kaip požiūrį – ne reakciją į aplinkybes, o sąmoningą pasirinkimą matyti grožį kasdienybėje."
    },
    4: {
      theme: "Struktūros ir pamatų",
      energy: "Mėnuo, kai statote pamatus. Energija kviečia į discipliną, tvarką ir nuoseklų darbą. Tai ne pats linksmiausias, bet vienas svarbiausių periodų – čia gimsta tvirtumas.",
      lightFlow: "Energija jums palankiausia, kai dirbate sistemingai, laikotės planų ir nesiekiate greitų rezultatų. Kantrybė ir nuoseklumas – jūsų raktai.",
      shadowFlow: "Atsargiai su pernelyg dideliu griežtumu sau ir kitiems. Taip pat vengite užsidarymo – struktūra neturi tapti kalėjimu.",
      conscious: "Visą šį mėnesį praktikuok nuoseklumą kaip vertybę. Ne tobulumą, o kasdienį atsidavimą tam, kas svarbu – po mažą žingsnį."
    },
    5: {
      theme: "Transformacijos ir laisvės",
      energy: "Pokyčių mėnuo. Energija reikalauja lankstumo ir atvirumo naujam. Kas buvo stabilu, gali judėti; kas atrodė aišku, gali keistis. Ir tai gerai.",
      lightFlow: "Energija jums palankiausia, kai sekate smalsumą, esate atviri netikėtumams ir nelaikotės pernelyg griežtai senų planų. Kelionės ir nauji žmonės – energijos šaltinis.",
      shadowFlow: "Atsargiai su perdėtu nerimu ir impulsyviais sprendimais. Taip pat vengite kraštutinumų – laisvė nereiškia chaoso.",
      conscious: "Visą šį mėnesį praktikuok atvirumą kaip gyvenimo būdą. Ne bėgimą nuo senojo, o sąmoningą atsivėrimą naujoms galimybėms."
    },
    6: {
      theme: "Meilės ir atsakomybės",
      energy: "Širdies mėnuo. Energija kviečia į meilę, rūpestį ir grožį. Santykiai, namai, šeima – visa, kas susiję su širdimi – šį mėnesį tampa centralia tema.",
      lightFlow: "Energija jums palankiausia, kai kuriate harmoniją aplinkoje, rūpinatės artimaisiais ir leidžiate sau džiaugtis grožiu. Meilė – ne silpnybė, o jėga.",
      shadowFlow: "Atsargiai su perdėtu savęs aukojimu ir kitų problemų nešimu. Taip pat vengite perfekcionizmo namuose ir santykiuose.",
      conscious: "Visą šį mėnesį praktikuok meilę kaip būseną – ne tik veiksmą, o nuolatinį širdies atvirumą sau ir kitiems."
    },
    7: {
      theme: "Vidinės kelionės",
      energy: "Dvasinės gilinimo mėnuo. Energija kviečia į tylą, refleksiją ir atsakymų ieškojimą viduje, ne išorėje. Tai laikas klausti: 'Kas aš iš tikrųjų esu?'",
      lightFlow: "Energija jums palankiausia, kai skirriate laiko vienatvei, skaitote, medituojate ir klausotės savo intuicijos. Išorinis pasaulis – ne prioritetas.",
      shadowFlow: "Atsargiai su perdėtu atsiribojimu ir intelektualine aroganija. Taip pat vengite naudoti 'dvasingumą' kaip bėgimą nuo gyvenimo.",
      conscious: "Visą šį mėnesį praktikuok vidinę tylą kaip prieglobstį. Ne bėgimą nuo pasaulio, o sąmoningą sugrįžimą prie savęs."
    },
    8: {
      theme: "Galios ir pasiekimų",
      energy: "Realizacijos mėnuo. Energija palaiko jūsų ambicijas, finansinius tikslus ir gebėjimą daryti poveikį materialiam pasauliui. Tai laikas veikti plačiai.",
      lightFlow: "Energija jums palankiausia, kai prisiimate atsakomybę, derėtis dėl to, ko norite, ir nebiote turėti. Galia – ne blogis, o įrankis.",
      shadowFlow: "Atsargiai su godumu ir manipuliacija. Taip pat vengite materializmo kaip tikslo savaime – pinigai yra energija, ne vertė.",
      conscious: "Visą šį mėnesį praktikuok atsakomybę kaip laisvę. Ne naštą, o sąmoningą pasirinkimą kurti savo realybę."
    },
    9: {
      theme: "Užbaigimo ir transformacijos",
      energy: "Pabaigų mėnuo, ruošiantis naujai pradžiai. Energija kviečia paleisti tai, kas nebetarnauja – santykius, įpročius, įsitikinimus, daiktus. Erdvė – dovana.",
      lightFlow: "Energija jums palankiausia, kai atleidžiate, užbaigiate projektus, dalinate ir padėdami kitiems. Jūsų dosnumas šį mėnesį grįžta daugeriopai.",
      shadowFlow: "Atsargiai su nostalgija ir laikymusi praeities. Taip pat vengite pradėti naujus didelius projektus – pirma užbaikite senus.",
      conscious: "Visą šį mėnesį praktikuok paleidimą kaip dovaną sau. Ne praradimą, o sąmoningą erdvės kūrimą naujai šviesai."
    }
  };
  
  return descriptions[number] || descriptions[1];
};

export default function ForecastPage() {
  const navigate = useNavigate();
  const { isVIP: authIsVIP, user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [isVIP, setIsVIP] = useState(false);
  const [showVIPModal, setShowVIPModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCalcInfo, setShowCalcInfo] = useState({ day: false, week: false, month: false, year: false });

  // Sync VIP status from auth context - CRITICAL: wait for loading and check user.isVIP
  useEffect(() => {
    if (!loading) {
      const vipStatus = authIsVIP || (user && user.isVIP);
      setIsVIP(vipStatus);
    }
  }, [authIsVIP, loading, user]);

  useEffect(() => {
    // Wait for auth loading to complete
    if (loading) return;
    
    // PRIORITY: Always check localStorage first (for "Mano ratas" member viewing)
    // This ensures selected member's data persists across all pages
    const storedData = localStorage.getItem('numerologyData');
    
    if (storedData) {
      const parsed = JSON.parse(storedData);
      setData(parsed);
      return;
    }
    
    // Fallback: If no localStorage, check if user is logged in with data
    if (user && user.day && user.month && user.year) {
      setData({
        firstName: user.firstName,
        lastName: user.lastName,
        day: user.day,
        month: user.month,
        year: user.year,
        lifePath: user.lifePath
      });
      return;
    }
    
    // No data found - redirect to calculator
    toast.error('Duomenų nerasta. Prašome pirmiau užpildyti formą.');
    navigate('/skaiciuoti');
  }, [navigate, user, loading]);

  const handleVIPActivated = (email) => {
    setIsVIP(true);
    setShowSuccessModal(true);
  };

  // Copy handler function
  const handleCopy = (text, label) => {
    copyToClipboard(text, label);
  };

  if (!data) return null;

  const today = new Date();
  const todayFormatted = today.toLocaleDateString('lt-LT', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();
  const personalYear = calculatePersonalYear(data.day, data.month, currentYear);
  const personalMonth = calculatePersonalMonth(personalYear, currentMonth);
  const personalDay = calculatePersonalDay(personalMonth, currentDay);

  const dailyAdvice = getDailyAdvice(personalDay);

  const monthNames = ['Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis', 
                      'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'];
  const currentMonthName = monthNames[currentMonth - 1];

  // Calculate week number of the month (1-4, capped at 4)
  const getWeekOfMonth = (date) => {
    const day = date.getDate();
    const weekNum = Math.ceil(day / 7);
    return Math.min(weekNum, 4); // Cap at 4 weeks
  };
  
  const weekOfMonth = getWeekOfMonth(today);
  
  // NEW FORMULA: Personal Week = Personal Month + Week Number (1, 2, 3, or 4)
  // Then reduce to single digit (or master number 11, 22)
  const calculatePersonalWeek = (personalMonth, weekNum) => {
    const sum = personalMonth + weekNum;
    if (sum === 11 || sum === 22) return sum;
    if (sum > 9) {
      const reduced = String(sum).split('').reduce((a, b) => parseInt(a) + parseInt(b), 0);
      if (reduced === 11 || reduced === 22) return reduced;
      return reduced > 9 ? String(reduced).split('').reduce((a, b) => parseInt(a) + parseInt(b), 0) : reduced;
    }
    return sum;
  };
  
  const personalWeek = calculatePersonalWeek(personalMonth, weekOfMonth);
  
  const weeklyEnergy = getWeeklyEnergyDescription(personalWeek, data.lifePath);
  const monthlyEnergy = getMonthlyEnergyDescription(personalMonth, data.lifePath);

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/rezultatai')}
              className="text-foreground/70 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Grįžti į rezultatus
            </Button>
            <UserMenu />
          </div>
          
          <div className="text-center mb-6">
            <h1 className="font-display text-3xl sm:text-4xl text-primary mb-2">
              Tavo Energijos Žemėlapis
            </h1>
            <p className="text-foreground/70 text-lg">
              {data.firstName}, atrask laiko kokybę ir savo vidinį kompasą
            </p>
          </div>
        </div>

        {/* Personal Year Context - The Foundation */}
        <Card className="card-mystical p-6 mb-8 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5">
          <div className="flex items-start gap-4">
            <Star className="w-8 h-8 text-amber-400 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display text-lg text-amber-400">
                  {currentYear} – Tavo Asmeniniai {personalYear} Metai
                </h3>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleCopy(
                    `${currentYear} – Asmeniniai ${personalYear} Metai\n\n${getPersonalYearTheme(personalYear).theme}\n\n${getPersonalYearTheme(personalYear).context}`,
                    'Asmeniniai Metai'
                  )}
                  className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-foreground/90 text-sm leading-relaxed mb-2">
                {getPersonalYearTheme(personalYear).theme}
              </p>
              <p className="text-foreground/70 text-sm leading-relaxed">
                {getPersonalYearTheme(personalYear).context}
              </p>
            </div>
          </div>
        </Card>

        {/* FREE - Daily Forecast */}
        <Card className="card-mystical p-8 mb-8 border-primary/30 shadow-glow">
          <div className="flex items-start gap-4 mb-6">
            <Sun className="w-10 h-10 text-primary flex-shrink-0 animate-pulse-glow" />
            <div className="flex-1">
              <div className="flex items-center">
                <h2 className="font-display text-2xl text-primary mb-2">
                  Dienos Vibracija
                </h2>
                <InfoButton onClick={() => setShowCalcInfo(prev => ({ ...prev, day: true }))} />
              </div>
              <p className="text-muted-foreground text-sm">
                {todayFormatted} • Asmeniniai {personalYear} metai
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-4xl font-display text-primary font-bold">
                {personalDay}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleCopy(
                  `Dienos Vibracija – ${todayFormatted}\nSkaičius: ${personalDay}\n\nŠiandienos energija:\n${dailyAdvice}\n\nSąmoningas pasirinkimas:\nŠiandien leisk sau būti tiksliai ten, kur esi. Tavo asmeninė dienos vibracija ${personalDay} kviečia į sąmoningą buvimą čia ir dabar.`,
                  'Dienos Vibracija'
                )}
                className="text-primary hover:text-primary-glow hover:bg-primary/10"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div className="bg-secondary/20 border border-primary/20 rounded-lg p-6">
            <h3 className="font-semibold text-lg text-foreground mb-3">
              Šiandienos energija
            </h3>
            <p className="text-foreground/90 leading-relaxed mb-4">
              {dailyAdvice}
            </p>
            <div className="mt-4 pt-4 border-t border-border/50">
              <p className="text-sm text-amber-400 font-medium">
                ✨ Sąmoningas pasirinkimas
              </p>
              <p className="text-foreground/80 text-sm mt-1">
                Šiandien leisk sau būti tiksliai ten, kur esi. Tavo asmeninė dienos vibracija {personalDay} kviečia į sąmoningą buvimą čia ir dabar.
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              🌟 Nemokama dienos energija atnaujinama kasdien
            </p>
          </div>
        </Card>

        {/* LOCKED WEEKLY FORECAST */}
        <Card className="card-mystical p-8 mb-8 border-border/50 relative overflow-hidden">
          <div className="flex items-start gap-4 mb-6">
            <Calendar className="w-10 h-10 text-primary flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center">
                <h2 className="font-display text-2xl text-primary mb-2">
                  Savaitės Energijos Srautas
                </h2>
                <InfoButton onClick={() => setShowCalcInfo(prev => ({ ...prev, week: true }))} />
              </div>
              <p className="text-muted-foreground text-sm">
                Skaičius {personalWeek} • {weekOfMonth}-oji mėnesio savaitė
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isVIP && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopy(
                      `Savaitės Energijos Srautas\nSkaičius: ${personalWeek}\n{weekOfMonth}-oji mėnesio savaitė\n\n🌊 Šios savaitės energija:\n${weeklyEnergy.energy}\n\n✦ Kur energija teka lengviausiai:\n${weeklyEnergy.flow}\n\n⚠ Galimas pasipriešinimas:\n${weeklyEnergy.resistance}\n\n✨ Sąmoningas pasirinkimas:\n${weeklyEnergy.conscious}`,
                      'Savaitės Prognozė'
                    )}
                    className="text-primary hover:text-primary-glow hover:bg-primary/10"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <div className="bg-primary/20 px-3 py-1 rounded-full">
                    <span className="text-xs font-semibold text-primary">VIP</span>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div className="relative">
            <div className={isVIP ? '' : 'blur-xl select-none pointer-events-none opacity-50'}>
              <div className="bg-secondary/20 border border-primary/20 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">🌊 Šios savaitės energija</h3>
                  <p className="text-foreground/90 leading-relaxed">{weeklyEnergy.energy}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-green-400 mb-2">✦ Kur energija teka lengviausiai</h4>
                  <p className="text-foreground/80 leading-relaxed">{weeklyEnergy.flow}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-orange-400 mb-2">⚠ Galimas pasipriešinimas</h4>
                  <p className="text-foreground/80 leading-relaxed">{weeklyEnergy.resistance}</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border/50">
                  <p className="text-sm text-amber-400 font-medium">✨ Sąmoningas pasirinkimas</p>
                  <p className="text-foreground/90 mt-1">{weeklyEnergy.conscious}</p>
                </div>
              </div>
            </div>
            
            {!isVIP && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-background/50 to-background cursor-pointer"
                onClick={() => setShowVIPModal(true)}
              >
                <div className="text-center px-6 py-8 max-w-md">
                  <Lock className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-primary mb-3 hover:text-amber-400 transition-colors">
                    Atrakink savaitės energiją
                  </h3>
                  <p className="text-foreground/80 mb-6 leading-relaxed">
                    Sužinok, kur tavo energija šią savaitę teka lengviausiai ir kur gali susidurti su pasipriešinimu.
                  </p>
                  <Button
                    onClick={(e) => { e.stopPropagation(); setShowVIPModal(true); }}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
                  >
                    Atrakinti
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* LOCKED MONTHLY FORECAST */}
        <Card className="card-mystical p-8 mb-8 border-border/50 relative overflow-hidden">
          <div className="flex items-start gap-4 mb-6">
            <TrendingUp className="w-10 h-10 text-primary flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center">
                <h2 className="font-display text-2xl text-primary mb-2">
                  Mėnesio Energijos Paveikslas
                </h2>
                <InfoButton onClick={() => setShowCalcInfo(prev => ({ ...prev, month: true }))} />
              </div>
              <p className="text-muted-foreground text-sm">
                {currentMonthName} • Skaičius {personalMonth} • {monthlyEnergy.theme} mėnuo
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isVIP && (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleCopy(
                      `Mėnesio Energijos Paveikslas\n${currentMonthName} • Skaičius ${personalMonth} • ${monthlyEnergy.theme} mėnuo\n\n🌙 Mėnesio energetinė tema:\n${monthlyEnergy.energy}\n\n☀️ Šviesos srautas:\n${monthlyEnergy.lightFlow}\n\n🌑 Šešėlio zona:\n${monthlyEnergy.shadowFlow}\n\n✨ Sąmoningas pasirinkimas:\n${monthlyEnergy.conscious}`,
                      'Mėnesio Prognozė'
                    )}
                    className="text-primary hover:text-primary-glow hover:bg-primary/10"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <div className="bg-primary/20 px-3 py-1 rounded-full">
                    <span className="text-xs font-semibold text-primary">VIP</span>
                  </div>
                </>
              )}
            </div>
          </div>
          
          <div className="relative">
            <div className={isVIP ? '' : 'blur-xl select-none pointer-events-none opacity-50'}>
              <div className="bg-secondary/20 border border-primary/20 rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">🌙 Mėnesio energetinė tema</h3>
                  <p className="text-foreground/90 leading-relaxed">{monthlyEnergy.energy}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-green-400 mb-2">☀️ Šviesos srautas</h4>
                  <p className="text-foreground/80 leading-relaxed">{monthlyEnergy.lightFlow}</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-purple-400 mb-2">🌑 Šešėlio zona</h4>
                  <p className="text-foreground/80 leading-relaxed">{monthlyEnergy.shadowFlow}</p>
                </div>
                
                <div className="mt-4 pt-4 border-t border-border/50">
                  <p className="text-sm text-amber-400 font-medium">✨ Sąmoningas pasirinkimas</p>
                  <p className="text-foreground/90 mt-1">{monthlyEnergy.conscious}</p>
                </div>
              </div>
            </div>
            
            {!isVIP && (
              <div 
                className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-transparent via-background/50 to-background cursor-pointer"
                onClick={() => setShowVIPModal(true)}
              >
                <div className="text-center px-6 py-8 max-w-md">
                  <Lock className="w-12 h-12 text-primary mx-auto mb-4" />
                  <h3 className="font-display text-2xl text-primary mb-3 hover:text-amber-400 transition-colors">
                    Giluminis mėnesio paveikslas
                  </h3>
                  <p className="text-foreground/80 mb-6 leading-relaxed">
                    Atrask šio mėnesio šviesos ir šešėlio zonas. Sužinok, kokios temos dominuos ir kaip jas panaudoti savo augimui.
                  </p>
                  <Button
                    onClick={(e) => { e.stopPropagation(); setShowVIPModal(true); }}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold"
                  >
                    Atrakinti
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Weekly Cycle Card */}
        <div className="mb-8">
          <WeeklyCycleCard lifePath={data.lifePath} weekNumber={personalWeek} isVIP={isVIP} />
        </div>

        {/* VIP Subscription Section - ONLY for non-VIP users */}
        {!isVIP && (
          <Card className="card-mystical p-8 mb-8 border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-purple-500/10">
            <div className="text-center">
              <Compass className="w-16 h-16 text-amber-400 mx-auto mb-4" />
              <h3 className="font-display text-2xl text-amber-400 mb-4">
                Atrakink savo vidinį kompasą
              </h3>
              <p className="text-foreground/90 leading-relaxed mb-6 max-w-2xl mx-auto">
                Gauk giluminį energetinį mėnesio paveikslą. Sužinok, kur tavo energija šiuo metu teka lengviausiai, 
                o kur gali susidurti su pasipriešinimu. Tai padės tau išvengti klaidų, veikiant prieš savo asmeninę 
                vibraciją, ir leis rinktis sąmoningą augimą.
              </p>
              <Button
                onClick={() => setShowVIPModal(true)}
                size="lg"
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold px-8 py-6 text-lg"
              >
                Noriu matyti visą paveikslą
              </Button>
            </div>
          </Card>
        )}
      </div>

      <VIPModal
        isOpen={showVIPModal}
        onClose={() => setShowVIPModal(false)}
        onSuccess={handleVIPActivated}
        userData={data}
      />
      
      <VIPSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        userName={data?.firstName}
        userGender={data?.gender}
      />

      {/* Calculation Info Modals */}
      <CalculationInfo
        title="Kaip apskaičiuota Dienos Vibracija?"
        isOpen={showCalcInfo.day}
        onClose={() => setShowCalcInfo(prev => ({ ...prev, day: false }))}
        formula={
          <>
            <p className="font-medium text-amber-400 mb-2">Formulė:</p>
            <p className="bg-slate-800/50 p-3 rounded font-mono text-xs mb-3">
              Asmeninė diena = Asmeninis mėnuo + Einamoji diena
            </p>
            <p className="mb-2"><strong>Pavyzdys ({data?.firstName}):</strong></p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Asmeninis mėnuo: <span className="text-amber-400">{personalMonth}</span></li>
              <li>Einamoji diena: <span className="text-amber-400">{currentDay}</span></li>
              <li>Suma: {personalMonth} + {currentDay} = <span className="text-amber-400">{personalDay}</span></li>
            </ol>
            <p className="text-xs text-foreground/60 mt-3 italic">
              Rezultatas redukuojamas iki vieno skaitmens (1-9).
            </p>
          </>
        }
      />

      <CalculationInfo
        title="Kaip apskaičiuota Savaitės Energija?"
        isOpen={showCalcInfo.week}
        onClose={() => setShowCalcInfo(prev => ({ ...prev, week: false }))}
        formula={
          <>
            <p className="font-medium text-amber-400 mb-2">Formulė:</p>
            <p className="bg-slate-800/50 p-3 rounded font-mono text-xs mb-3">
              Asmeninė savaitė = Asmeninis mėnuo + Savaitės numeris (1-4)
            </p>
            <p className="mb-2"><strong>Pavyzdys ({data?.firstName}):</strong></p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Asmeninis mėnuo: <span className="text-amber-400">{personalMonth}</span></li>
              <li>Savaitės numeris mėnesyje: <span className="text-amber-400">{weekOfMonth}</span></li>
              <li>Suma: {personalMonth} + {weekOfMonth} = <span className="text-amber-400">{personalMonth + weekOfMonth}</span></li>
              {personalMonth + weekOfMonth > 9 && personalMonth + weekOfMonth !== 11 && personalMonth + weekOfMonth !== 22 && (
                <li>Redukuota: <span className="text-amber-400">{personalWeek}</span></li>
              )}
            </ol>
            <div className="mt-4 pt-3 border-t border-slate-700">
              <p className="text-xs text-foreground/70 mb-2">📊 Visos 4 savaitės šį mėnesį:</p>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { week: 1, dates: '1–7 d.' },
                  { week: 2, dates: '8–14 d.' },
                  { week: 3, dates: '15–21 d.' },
                  { week: 4, dates: 'nuo 22 d.' }
                ].map(({ week, dates }) => {
                  const weekNum = calculatePersonalWeek(personalMonth, week);
                  const isCurrentWeek = week === weekOfMonth;
                  return (
                    <div 
                      key={week} 
                      className={`flex flex-col items-center justify-center p-2 rounded ${isCurrentWeek ? 'bg-amber-500/30 border border-amber-500/50' : 'bg-slate-800/50'}`}
                    >
                      <div className={`text-[10px] ${isCurrentWeek ? 'text-amber-300' : 'text-foreground/50'}`}>
                        {week} sav.
                      </div>
                      <div className={`text-lg font-bold leading-tight ${isCurrentWeek ? 'text-amber-400' : 'text-foreground/80'}`}>
                        {weekNum}
                      </div>
                      <div className={`text-[9px] ${isCurrentWeek ? 'text-amber-300/80' : 'text-foreground/40'}`}>
                        ({dates})
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-xs text-foreground/60 mt-3 italic">
              Kiekviena savaitė turi unikalią energiją, kuri remiasi asmeninio mėnesio skaičiumi.
            </p>
          </>
        }
      />

      <CalculationInfo
        title="Kaip apskaičiuota Mėnesio Energija?"
        isOpen={showCalcInfo.month}
        onClose={() => setShowCalcInfo(prev => ({ ...prev, month: false }))}
        formula={
          <>
            <p className="font-medium text-amber-400 mb-2">Formulė:</p>
            <p className="bg-slate-800/50 p-3 rounded font-mono text-xs mb-3">
              Asmeninis mėnuo = Asmeniniai metai + Einamasis mėnuo
            </p>
            <p className="mb-2"><strong>Pavyzdys ({data?.firstName}):</strong></p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Gimimo diena: <span className="text-amber-400">{data?.day}</span></li>
              <li>Gimimo mėnuo: <span className="text-amber-400">{data?.month}</span></li>
              <li>Asmeniniai {currentYear} metai: <span className="text-amber-400">{personalYear}</span></li>
              <li>Asmeninis {currentMonthName}: <span className="text-amber-400">{personalMonth}</span></li>
            </ol>
            <p className="text-xs text-foreground/60 mt-3 italic">
              Asmeniniai metai = Gimimo diena + Gimimo mėnuo + Metų skaitmenų suma
            </p>
          </>
        }
      />
    </div>
  );
}
